import { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role = 'client') => {
    const response = await api.post('/auth/register', { email, password, name, role });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Check if user has a specific permission
  const hasPermission = (permissionKey) => {
    if (!user) return false;
    if (user.role === 'admin') return true; // Admin has all permissions
    if (user.permissions?.includes('all')) return true;
    return user.permissions?.includes(permissionKey) || false;
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissionKeys) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (user.permissions?.includes('all')) return true;
    return permissionKeys.some(key => user.permissions?.includes(key));
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAdmin: user?.role === 'admin',
    isOperator: user?.role === 'operator',
    isClient: user?.role === 'client',
    hasPermission,
    hasAnyPermission,
    permissions: user?.permissions || [],
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
